<template>
  <div
    ref="container"
    v-resize:throttle="onResize"
    class="full-width"
  >
    <!-- Header -->
    <div
      v-if="showHeaderContainer"
      :class="headerClasses"
    >
      <!-- Table Controls -->
      <TableControls
        v-if="showTableControlsToolbar"
        :with-dismissible-filters="withDismissibleFilters"
        :current-search="currentSearch"
        :current-filters="currentFilters"
        :table-mode="showTable"
        :with-search="withTableControlSearch"
        :with-control-buttons="withTableControlButtons"
        :search-debounce="searchDebounce"
        :search-autofocus="searchAutofocus"
        :search-input-props="searchInputProps"
        @dismiss="onDismiss"
        @clear="$emit('clearFilters')"
        @update:current-search="$emit('update:currentSearch', $event)"
      >
        <template v-if="withTableControlButtons && !!$slots['table-control-buttons']" #table-control-buttons>
          <slot name="table-control-buttons" />
        </template>
      </TableControls>

      <slot
        name="header"
        :show-table-headers-toolbar="showTableHeadersToolbar"
      />

      <!-- Header table columns -->
      <q-item v-if="showTableHeadersToolbar">
        <q-item-section>
          <div class="flex full-width items-center break-word no-wrap">
            <div
              v-if="withSelection && selectionMixin"
              class="relative-position text-center"
              style="flex: 0 0 40px;"
            >
              <q-checkbox
                v-if="withSelectAll"
                class="absolute-left"
                :model-value="isResults ? selectionMixin.isAllResultsSelected() : selectionMixin.isAllSelected()"
                :class="list.length <= 1 ? 'light-dimmed' : ''"
                :disable="list.length <= 1 || !clickable"
                @update:model-value="$emit('update:select-all', $event)"
              />
            </div>
            <HeaderCell
              v-for="(header, key) in visibleHeaders"
              :key="key"
              :header="header"
              :current-sort="currentSort"
              @click="onColumnSort(header)"
            />
          </div>
        </q-item-section>
      </q-item>
    </div>

    <slot
      name="before"
      :show-table="showTable"
    />

    <!-- Table mode -->
    <template v-if="showTable">
      <!-- Data -->
      <template v-if="!loading">
        <Group
          v-for="(rows, groupName, index) in groupedList"
          :key="groupName"
          :list-index="index"
          :separator="separator"
          :table-component="tableComponent"
          :table-props="tableProps"
        >
          <template v-if="isGrouped" #label>
            <slot
              name="group-name"
              :group="groupName"
              :list-mode="false"
              :rows="rows"
            >
              <sticky-header static color="black">
                <span class="text-h5">{{ groupName }}</span>
              </sticky-header>
            </slot>
          </template>
          <component
            :is="rowComponent"
            v-for="(row, key) in rows"
            :key="groupName + '-' + key"
            :clickable="clickable"
            :class="['module-item', selectionMixin && selectionMixin.isRowSelected(row.id) ? 'bg-blue-1' : '']"
            v-bind="rowProps(row)"
            @click="$emit('click', row)"
          >
            <q-item-section>
              <div class="flex full-width items-center break-word no-wrap">
                <div
                  v-if="withSelection && selectionMixin"
                  class="relative-position text-center"
                  style="flex: 0 0 40px;"
                >
                  <q-checkbox
                    :model-value="selectionMixin.isRowSelected(row.id)"
                    class="absolute-left"
                    @update:model-value="selectionMixin.doSelect($event, row.id)"
                  />
                </div>
                <RowCell
                  v-for="(header, k) in visibleHeaders"
                  :key="k"
                  :header="header"
                >
                  <slot
                    v-if="$slots['field-' + header.key]"
                    :name="'field-' + header.key"
                    :small="responsiveWidth === 'none' || (typeof responsiveWidth === 'number' && width < responsiveWidth)"
                    :row="row"
                  />
                  <div
                    v-else
                    :class="{'text-ellipsis': header.wrap === false}"
                  >
                    <span v-html="row[header.key]" />
                  </div>
                </RowCell>
              </div>
            </q-item-section>
          </component>
        </Group>
      </template>
    </template>

    <!-- List mode -->
    <template v-else>
      <template v-if="!loading && list.length > 0">
        <Group
          v-for="(rows, groupName, index) in groupedList"
          :key="groupName"
          :list-class="groupClass"
          :list-index="index"
          :separator="separator"
        >
          <template v-if="isGrouped" #label>
            <slot
              name="group-name"
              :group="groupName"
              :list-mode="true"
              :rows="rows"
            >
              <sticky-header :anchor="anchor" color="black">
                {{ groupName }}
              </sticky-header>
            </slot>
          </template>
          <slot
            v-for="row in rows"
            name="row"
            :row="row"
          />
        </Group>
      </template>
    </template>

    <template v-if="loading">
      <slot
        v-if="$slots.loading"
        name="loading"
      />
      <q-list
        v-else
        :separator="separator"
      >
        <DefaultSkeleton
          v-for="i in skeletonRows"
          :key="i"
          :headers="visibleHeaders"
          :with-selection="withSelection && showTable"
        />
      </q-list>
    </template>

    <div v-if="list.length < 1 && !loading">
      <no-results v-if="!$slots['no-results']" />
      <slot
        v-else
        name="no-results"
      />
    </div>

    <slot v-if="pagination" name="pagination">
      <q-pagination
        class="flex justify-center q-py-md"
        :model-value="page"
        :max="Math.ceil(totalResults / showingResults)"
        :max-pages="5"
        boundary-links
        direction-links
        input
        @update:model-value="onChangePage"
      />
    </slot>

    <!-- Infinite scroll sentinel: sits just below the list and asks the parent
         to load the next page as it nears the bottom. Disabled while loading or
         once everything is loaded. -->
    <q-infinite-scroll
      v-if="infinite"
      ref="infiniteScroll"
      :offset="300"
      :disable="loading || list.length >= totalResults"
      @load="onInfiniteLoad"
    >
      <template #loading>
        <div class="flex justify-center q-py-md">
          <q-spinner-dots
            color="primary"
            size="40px"
          />
        </div>
      </template>
    </q-infinite-scroll>
  </div>
</template>

<script>
import HeaderCell from './dynamictable/TableHeaderCell'
import RowCell from './dynamictable/TableRowCell'
import Group from './dynamictable/TableGroup'
import DefaultSkeleton from './dynamictable/DefaultSkeleton'
import { QItem } from 'quasar'
import { stickyValidator, stickyClasses } from 'src/helpers/sticky'
import scrollparent from 'src/helpers/scrollparent'
import { markRaw } from 'vue'
import { strToObj } from 'src/helpers/globals'

import resize from 'vue3-resize-directive'
import TableControls from 'components/partials/tables/dynamictable/TableControls.vue'

/**
 * DynamicTable can switch between a list item and a table-format depending on the available
 * screen real-estate
 *
 * SLOTS:
 * - loading: Content to display when table is loading
 * - no-results: Override default implementation when no results are given
 * - [field-header_key] (small, row): content to display for the given field key
 * - row (row): Item to display when in mobile/responsive mode
 * - group-name (group): Manipulate how each grouped category name is displayed
 * - table-control-buttons: When table controls are enabled, append content to end of toolbar
 * - before: Content to display before rendered table results and after the header
 * - header: Content to inject into the sticky header after TableControls and before columns
 */
export default {
  directives: {
    resize
  },
  components: {
    TableControls,
    Group,
    HeaderCell,
    RowCell,
    DefaultSkeleton
  },
  inject: {
    selectionMixin: {
      default: () => { }
    }
  },
  props: {
    /**
     * Array of headers (in display order). Each header should contain:
     * - key: mapping to the data key
     * - label: Label of header
     * - width: Width of the element (default 300px)
     * - display_width: optional table width at which the column will display
     * - fixed: boolean, is the width fixed at the specified width (default false)
     * - align: alignment of the text (left, center, right) (default left)
     * - wrap: wrap cell content to a new line (default true)
     * - sortable: column is sortable by clicking (default false)
     */
    headers: {
      type: Array,
      required: true
    },
    /**
     * Array of data to load into the list
     * Each row should have key-value pair mappings
     */
    list: {
      type: Array,
      required: true
    },
    // Current sorted column
    currentSort: {
      type: Object,
      default: null
    },
    // Current search query
    currentSearch: {
      type: String,
      default: null
    },
    // Current applied filters
    currentFilters: {
      type: Object,
      default: null
    },
    // Show separator between rows
    separator: {
      type: Boolean,
      default: false
    },
    // Make each row clickable
    clickable: {
      type: Boolean,
      default: false
    },
    // Width at which the table will display
    // String "none" will force table into mobile mode
    responsiveWidth: {
      type: [Number, String],
      default: 768
    },
    // Is data loading
    loading: {
      type: Boolean,
      default: false
    },
    /**
     * How the sticky header should be anchored. One of:
     * (none, page, page-menubar, dialog, dialog-menubar)
     */
    anchor: {
      type: String,
      validator: stickyValidator,
      default: 'page'
    },
    // Enable built-in pagination
    pagination: {
      type: Boolean,
      default: false
    },
    // Enable infinite scroll (auto-load next page near the bottom). When set,
    // the parent should append results on @load-more and the pagination control
    // is not shown. Mutually exclusive with `pagination`.
    infinite: {
      type: Boolean,
      default: false
    },
    // Enable table controls
    withTableControls: {
      type: Boolean,
      default: false
    },
    // Enable additional button slots for table control toolbar
    withTableControlButtons: {
      type: Boolean,
      default: false
    },
    // Enable search button in table control
    withTableControlSearch: {
      type: Boolean,
      default: true
    },
    // Enable dismissible filter q-chips
    withDismissibleFilters: {
      type: Boolean,
      default: false
    },
    // v-model, current page number
    page: {
      type: Number,
      default: 1
    },
    // number of results being displayed
    showingResults: {
      type: Number,
      default: 1
    },
    // Total result size
    totalResults: {
      type: Number,
      default: 0
    },
    // Disable automatically scrolling to the top when paginating
    scrolltop: {
      type: Boolean,
      default: true
    },
    // Function used for grouping the elements
    // param: item => recursively used to manipulate each item and fetch its correct group
    groupFunc: {
      type: Function,
      default: null
    },
    // Classes to apply to the outer container for the group of elements
    // Only applies for responsive mode (table mode remains unaffected)
    groupClass: {
      type: [String, Object]
    },
    // Classes to apply to the sticky header container which wraps the TableControls and columns
    headerClass: {
      type: [String, Object]
    },
    // Dynamically define the outer component which wraps each row in table mode
    rowComponent: {
      type: [Function, Object, String],
      default: markRaw(QItem)
    },
    // Define custom row props if a custom rowComponent is defined (param "row")
    rowProps: {
      type: Function,
      default: () => {
        return {}
      }
    },
    // Dynamically define the outer component which wraps the entire table
    tableComponent: {
      type: [Function, Object, String]
    },
    // Define custom props on the tableComponent
    tableProps: {
      type: Object
    },
    // Define custom props for the search input box
    searchInputProps: {
      type: Object
    },
    // Show selection handles
    withSelection: {
      type: Boolean,
      default: false
    },
    // Allow ability to select all rows if selection is enabled
    withSelectAll: {
      type: Boolean,
      default: true
    },
    isResults: {
      type: Boolean,
      default: false
    },
    // autofocus for search input if visible
    searchDebounce: {
      type: Number,
      default: undefined
    },
    // Autofocus (only on desktop) if search input visible
    searchAutofocus: {
      type: Boolean,
      default: undefined
    }
  },
  emits: ['click', 'sortFromColumn', 'dismissFilter', 'clearFilters', 'update:page', 'update:currentSearch', 'update:select-all', 'update:show-table', 'load-more'],
  data () {
    return {
      width: 0
    }
  },
  computed: {
    isGrouped () {
      return !!this.groupFunc
    },
    groupedList () {
      if (!this.isGrouped) {
        return { null: this.list }
      }

      return this.groupBy(this.list, this.groupFunc)
    },
    skeletonRows () {
      return Math.ceil(this.showingResults / 2)
    },
    headerClasses () {
      return stickyClasses(this.anchor, strToObj(this.headerClass, { 'blurred-container': true }))
    },
    visibleHeaders () {
      return this.headers.filter(item => !item.display_width || item.display_width <= this.width)
    },
    showTable () {
      return typeof this.responsiveWidth === 'number' && this.width >= this.responsiveWidth
    },
    showTableControlsToolbar () {
      return this.withTableControls && (this.withTableControlSearch || this.withTableControlButtons)
    },
    showTableHeadersToolbar () {
      return this.showTable && this.visibleHeaders.length > 0
    },
    showHeaderContainer () {
      return this.showTableHeadersToolbar || this.showTableControlsToolbar
    }
  },
  watch: {
    'showTable' (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$emit('update:show-table', newVal)
      }
    },
    'list' () {
      if (this.scrolltop && this.pagination) {
        this.$nextTick(() => {
          window.scrollTo(0, 0)
        })
      }
    }
  },
  methods: {
    // q-infinite-scroll asks for more — hand the `done` callback to the parent,
    // which fetches/appends the next page and calls done(stop) when finished.
    onInfiniteLoad (index, done) {
      this.$emit('load-more', done)
    },
    // Let the parent reset the infinite scroll after a fresh load (new search /
    // filter / sort) so it re-evaluates whether more can be loaded.
    resetInfinite () {
      this.$nextTick(() => {
        if (this.$refs.infiniteScroll) {
          this.$refs.infiniteScroll.reset()
          this.$refs.infiniteScroll.resume()
        }
      })
    },
    onResize (e) {
      let width = e.offsetWidth

      // Conditionally add 7 pixels to the width of the container is scrolling
      // This resolves an issue where the table would infinitely flicker if switching between mobile/desktop modes
      //  with content which just hits the Y scroll limit
      // 7 pixels because the scroll bar width is 7px
      if (typeof this.responsiveWidth === 'number') {
        const scroller = scrollparent(this.$refs.container)
        if ((document.body === scroller && scroller.scrollHeight > window.innerHeight) || scroller.scrollHeight > scroller.clientHeight) {
          width += 7
        }
      }

      this.width = width
    },
    onChangePage (val) {
      this.$emit('update:page', val)
    },
    onColumnSort (header) {
      if (header.sortable) {
        this.$emit('sortFromColumn', header)
      }
    },
    onDismiss (key, value) {
      this.$emit('dismissFilter', key, value)
    }
  }
}
</script>
